import { useState } from "react";
import { Flame, RefreshCw } from "lucide-react";

import { PageHeader, btn, Loader } from "@/components/Page";
import { useMiningDashboard } from "@/hooks/useMiningDashboard";
import { MiningTabs } from "@/components/mining/MiningTabs";
import { MiningSummaryPanel } from "@/components/mining/MiningSummaryPanel";
import { MiningResourcesPanel } from "@/components/mining/MiningResourcesPanel";
import { MiningSmeltingPanel } from "@/components/mining/MiningSmeltingPanel";
import { MiningConfirmDialog } from "@/components/mining/MiningConfirmDialog";
import { MiningEquipmentLink } from "@/components/mining/MiningEquipmentLink";

export default function MiningSmelting() {
  const [confirm, setConfirm] = useState(null);
  const { data, loading, loadMining, smeltBusyKey, handleSmelt } = useMiningDashboard();

  const confirmBusy = confirm ? smeltBusyKey === `${confirm.rarity}-${confirm.kind}` : false;

  if (loading && !data) return <Loader label="Loading Smelting..." />;

  return (
    <div data-testid="mining-smelting-page" className="space-y-6">
      <PageHeader
        title="Smelting"
        subtitle="Convert raw ore into normal weapon or armor fragments used by the blacksmith."
        action={
          <button data-testid="mining-smelting-refresh-button" type="button" onClick={() => loadMining()} className={btn("ghost") + " flex items-center gap-2"}>
            <RefreshCw size={15} /> Refresh
          </button>
        }
      />

      <MiningEquipmentLink />

      <MiningTabs claimableCount={data?.discoveries?.claimable_count || 0} />

      <section data-testid="mining-smelting-explainer" className="border border-amber-900/30 bg-zinc-950/70 p-4 rounded-sm">
        <h2 className="font-cinzel text-base text-amber-300 uppercase tracking-wide">How smelting works</h2>
        <div className="mt-3 grid md:grid-cols-3 gap-3 text-xs text-zinc-400">
          <div className="border border-zinc-800 bg-zinc-900/50 p-3 rounded-sm">1. Raw ore is the mining-only material you collect from mining expeditions.</div>
          <div className="border border-zinc-800 bg-zinc-900/50 p-3 rounded-sm">2. Smelting turns that raw ore into normal <span className="text-zinc-100">weapon</span> or <span className="text-zinc-100">armor</span> fragments.</div>
          <div className="border border-zinc-800 bg-zinc-900/50 p-3 rounded-sm">3. Those normal fragments are the same ones your blacksmith already uses for equipment crafting.</div>
        </div>
      </section>

      <div className="grid xl:grid-cols-[1fr_1fr] gap-6">
        <MiningSummaryPanel profile={data?.profile} smithing={data?.smithing} />
        <MiningResourcesPanel profile={data?.profile} showHistory={false} />
      </div>

      <MiningSmeltingPanel
        recipes={data?.smelting_recipes || []}
        equipmentFragments={data?.equipment_fragments}
        smeltBusyKey={smeltBusyKey}
        onSmelt={(rarity, kind, batches) => {
          const recipe = (data?.smelting_recipes || []).find((entry) => entry.rarity === rarity);
          if (!recipe) return;
          const totalOre = recipe.ore_cost * batches;
          const totalFragments = recipe.output_fragments * batches;
          const kindLabel = kind === "weapon" ? "Weapon" : "Armor";
          setConfirm({
            type: "smelt",
            rarity,
            kind,
            title: `Confirm ${kindLabel} Smelting`,
            message: `Create ${totalFragments} ${rarity} ${kind} fragment${totalFragments === 1 ? "" : "s"}?\n\nThis will use ${totalOre} ${rarity} ore.`,
            confirmLabel: `Create ${kindLabel} Fragments`,
            onConfirm: async () => {
              await handleSmelt(rarity, kind, batches);
              setConfirm(null);
            },
          });
        }}
      />

      <MiningConfirmDialog confirm={confirm} busy={confirmBusy} onClose={() => setConfirm(null)} />
    </div>
  );
}